import type { FetchPrecondition } from '../../providers/DataSourceManager.types';

/**
 * Where one decision input's value comes from on this page. `source` is a
 * data source name or a reserved root (`context` for host-supplied facts;
 * `formState`; `requestContext` for a visitor fact the page host supplies).
 * `path` reads into it with dots and `[n]` indexes, for example
 * `listing.dealer.id`.
 */
export type FactBinding = {
  readonly factKey: string;
  readonly source: string;
  readonly path: string;
  /**
   * The fact is bound only while at least one of these holds. Absent means
   * always bound. When every binding to a data source is conditional, the
   * source is fetched only while one of them holds.
   */
  readonly when?: readonly FetchPrecondition[];
};

/** A binding the page refused, and why; its fact is absent. */
export type RefusedBinding = {
  readonly factKey: string;
  readonly reason: string;
};

/** Every fact binding declared by the page's variants. */
export type FactBindings = {
  /** One binding per fact across the page; the first declaration wins. */
  readonly bindings: readonly FactBinding[];
  /** Bindings dropped because their precondition is not allowed. */
  readonly refused: readonly RefusedBinding[];
};

export const NO_FACT_BINDINGS: FactBindings = { bindings: [], refused: [] };

/** The decisions a page's variants name. */
export type DecisionPlan = {
  /** Every variant's decision id, nested ones included, first-seen order. */
  readonly decisionIds: readonly string[];
};

export const EMPTY_DECISION_PLAN: DecisionPlan = { decisionIds: [] };
